<script lang="ts">
	import { Helper, Label, Textarea } from "flowbite-svelte-v1";
	import { twMerge } from "tailwind-merge";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		onsave: (tokens: string[]) => void;
		isBulkEditing: boolean;
		options: string[];
	}

	let { onsave, isBulkEditing = $bindable(), options }: Props = $props();

	const fieldId = $props.id();

	let scratch = $state(options.join("\n"));
	let error = $state("");

	const parsed = $derived(parseOptions(scratch));
	const duplicates = $derived(duplicateOptions(parsed));

	function parseOptions(text: string): string[] {
		return text
			.split(/\r?\n/)
			.map(line => line.trim())
			.filter(Boolean);
	}

	function duplicateOptions(values: string[]): string[] {
		const counts: Record<string, number> = {};
		for (const value of values) {
			counts[value] = (counts[value] ?? 0) + 1;
		}
		return [...new Set(values.filter(value => (counts[value] ?? 0) > 1))];
	}

	function handleSave() {
		if (!validate()) {
			return;
		}
		onsave(parsed);
		isBulkEditing = false;
	}

	function validate(): boolean {
		if (parsed.length === 0) {
			error = "Enter at least one option.";
			return false;
		}
		if (duplicates.length > 0) {
			error = `Input contains duplicate values: ${duplicates.join(", ")}`;
			return false;
		}
		error = "";
		return true;
	}

	function cancel() {
		isBulkEditing = false;
	}
</script>

<div class="flex w-full flex-col gap-2">
	<Label for={`${fieldId}-options`} class="text-sm font-medium text-gray-900">Enter options</Label>
	<Textarea rows={6} id={`${fieldId}-options`} class={twMerge("block h-40 w-full focus:ring-inset", error && "border-red-700")} classes={{ div: "w-full" }} bind:value={scratch} oninput={() => (error = "")} />
	{#if error}
		<Helper class="text-red-700">
			{error}
		</Helper>
	{:else}
		<Helper>Type or paste a list of options, separated by a new line</Helper>
	{/if}
	<div class="flex w-full flex-row items-center justify-end gap-2">
		<button type="button" class="text-primary-700 text-sm font-medium hover:underline" data-action-name="choice-bulk-cancel" onclick={cancel}>Cancel</button>
		<Button onclick={handleSave} data-action-name="choice-bulk-options-editor-save">Save</Button>
	</div>
</div>
